import type { DropdownOption } from '../../../components/Dropdown/DropdownOption/DropdownOption';
import type { UseAppForm } from '../../form';
import type { SpaceSelectorItemField } from './SpaceSelectorItem/SpaceSelectorItem';

import { useStore } from '@tanstack/react-form';
import { use, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../lib/globals';
import { getTestProps } from '../../../lib/helpers';

import Dropdown from '../../../components/Dropdown/Dropdown';
import HelpErrorTextsTemplate from '../../../components/HelpErrorTextsTemplate/HelpErrorTextsTemplate';

import UserContext from '../../../contexts/UserContext';

import { useFieldContext, useFormContext } from '../../form';
import { useFieldErrors } from '../../useFieldErrors';

interface SpaceSelectorFieldProps {
  /**
   * Available spaces for selection
   */
  spaces?: DropdownOption[];
  /**
   * User roles by space
   */
  userRolesBySpace?: Record<string, DropdownOption[]>;
  /**
   * Whether the field is disabled
   */
  disabled?: boolean;
  /**
   * Test ID for testing purposes
   */
  testId?: string;
}

const SpaceSelectorField = ({
  spaces = EMPTY_ARRAY,
  userRolesBySpace = EMPTY_OBJECT,
  disabled = false,
  testId,
}: SpaceSelectorFieldProps) => {
  const { t } = useTranslation();
  const form = useFormContext() as unknown as UseAppForm;
  const field = useFieldContext<SpaceSelectorItemField[]>();
  const errors = useFieldErrors(field.name);

  const { isAdmin } = use(UserContext);

  const value = useMemo(() => field.state.value || [], [field.state.value]);

  const removeSpace = useCallback(
    (idx: number) => {
      field.removeValue(idx);
    },
    [field],
  );

  const onSpaceAdd = useCallback(
    (_: unknown, value: string) => {
      field.pushValue({ id: value, userRoles: [] });
      field.handleBlur();
      return '';
    },
    [field],
  );

  const selectedIds = useStore(form.store, ({ values }) =>
    (
      ((values as Record<string, any>)[
        field.name
      ] as SpaceSelectorItemField[]) || []
    ).map(({ id }) => id),
  );

  const availableSpaces = useMemo(
    () => spaces.filter(({ value }) => !selectedIds.includes(value)),
    [selectedIds, spaces],
  );

  return (
    <div className="flex flex-col gap-8 md:gap-4">
      {value.map((_, idx) => (
        <form.AppField key={idx} name={`${field.name}[${idx}]`}>
          {(field) => (
            <field.SpaceSelectorItem
              idx={idx}
              onRemove={removeSpace}
              availableSpaces={availableSpaces}
              allspaces={spaces}
              userRolesBySpace={userRolesBySpace}
              disabled={disabled}
              {...getTestProps(testId, `${idx}`, 'testId')}
            />
          )}
        </form.AppField>
      ))}

      {isAdmin && (
        <div className="flex flex-col items-center md:flex-row md:justify-between md:items-start gap-2 md:mr-11">
          <Dropdown
            value=""
            options={availableSpaces}
            placeholder={t('Spaces.SpaceSelector.PlaceHolder')}
            emptyOptions={
              <div className="py-2 px-4 dark:text-slate-200">
                {t('Spaces.SpaceSelector.EmptySpaces')}
              </div>
            }
            additionalClasses="w-full"
            disabled={disabled}
            onChange={onSpaceAdd}
            hideSearch
            {...getTestProps(testId, 'space-placeholder', 'testId')}
          />
          <Dropdown
            options={EMPTY_ARRAY}
            placeholder={t('UserRoles.RoleSelector.PlaceHolder')}
            additionalClasses="w-full"
            disabled={true}
          />
        </div>
      )}
      <HelpErrorTextsTemplate error={errors} />
    </div>
  );
};

export default SpaceSelectorField;
